import React, { useContext } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, Tooltip } from "@mui/material";

import { ROW_MIN_HEIGHT } from "components/sensors/table/table-layout";
import { SnackbarContext } from "context/snackbar-context";

const COPY_ICON_FONT_SIZE = "0.75rem";

// Lets a click-to-copy cell (a non-button element with onClick) also be
// activated via keyboard, since Enter/Space don't trigger onClick natively
// on a div/span the way they do on a real <button>.
function copyOnEnterOrSpace(onCopy: () => void) {
  return (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onCopy();
    }
  };
}

const CopyableCell = ({
  value,
  children,
  tooltip,
  tabIndex,
}: {
  value: string;
  // Overrides what's displayed (e.g. a MonoText-wrapped value, or a
  // noValueText placeholder) without changing what's copied/announced.
  children?: React.ReactNode;
  tooltip?: string;
  // DataGrid manages a roving tabindex across cells -- forward the value it
  // passes to renderCell instead of hardcoding 0, so only the grid's current
  // cell is a tab stop (see GridRenderCellParams.tabIndex).
  tabIndex?: 0 | -1;
}) => {
  const { copyToClipboard } = useContext(SnackbarContext);

  const cell = !value ? (
    // Tooltip needs a real DOM element to attach hover/focus listeners to
    // (a Fragment forwards no ref) -- span keeps this inline, same as the
    // direct noValueText usage elsewhere.
    <span>{children}</span>
  ) : (
    <Box
      onClick={() => copyToClipboard(value)}
      onKeyDown={copyOnEnterOrSpace(() => copyToClipboard(value))}
      role="button"
      tabIndex={tabIndex ?? 0}
      aria-label={`Copy ${value}`}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      minHeight={ROW_MIN_HEIGHT}
      textAlign="left"
      sx={{
        cursor: "pointer",
        "& .copy-icon": { visibility: "hidden" },
        "&:hover .copy-icon, &:focus-visible .copy-icon": {
          visibility: "visible",
        },
      }}
    >
      <Box
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {children ?? value}
      </Box>
      <ContentCopyIcon
        className="copy-icon"
        sx={{
          color: "text.disabled",
          flexShrink: 0,
          fontSize: COPY_ICON_FONT_SIZE,
        }}
      />
    </Box>
  );

  return tooltip ? <Tooltip title={tooltip}>{cell}</Tooltip> : cell;
};

export default CopyableCell;
